/**
 * CR162 (updated): Stuck Run Banner
 * Shows contextual banners based on stuck type:
 * - IDLE: "Auto steps paused" with Resume action
 * - BLOCKED_BY_FAILURE: "Needs attention" with Retry/Skip actions
 */

import { useState } from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  AlertCircle,
  Clock,
  Copy,
  ExternalLink,
  Loader2,
  Pause,
  PlayCircle,
  RotateCcw,
  SkipForward,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import {
  type BatchRunStep,
  type StuckType,
  determineStuckType,
  buildStuckRunDiagnostics,
  getEarliestFailedAutoStep,
  getNextResumableAutoStep,
} from "@/lib/batch-runs/stuckRunTypes";

// Re-export for backward compatibility
export { determineStuckType, getNextResumableAutoStep };
export type { BatchRunStep };

interface StuckRunBannerProps {
  batchRunId: string;
  runStatus: string;
  steps: BatchRunStep[];
  configJson: Record<string, unknown>;
  sourcesEnabled: string[];
  onRecoveryAction?: () => void;
}

/**
 * Legacy compatibility function
 */
export function isRunStuck(runStatus: string, steps: BatchRunStep[]): boolean {
  const stuckType = determineStuckType(runStatus, steps);
  return stuckType === "IDLE" || stuckType === "BLOCKED_BY_FAILURE";
}

/**
 * Legacy compatibility function
 */
export function getFirstStuckAutoStep(
  steps: BatchRunStep[],
  sourcesEnabled: string[]
): BatchRunStep | null {
  // First check for failed steps (they take priority)
  const failedStep = getEarliestFailedAutoStep(steps);
  if (failedStep) return failedStep;

  // Then check for resumable steps
  return getNextResumableAutoStep(steps, sourcesEnabled);
}

export function StuckRunBanner({
  batchRunId,
  runStatus,
  steps,
  configJson,
  sourcesEnabled,
  onRecoveryAction,
}: StuckRunBannerProps) {
  const { toast } = useToast();
  const [isRetrying, setIsRetrying] = useState(false);
  const [isResuming, setIsResuming] = useState(false);
  const [showSkipDialog, setShowSkipDialog] = useState(false);
  const [skipReason, setSkipReason] = useState("");
  const [isSkipping, setIsSkipping] = useState(false);

  const stuckType = determineStuckType(runStatus, steps);
  const diagnostics = buildStuckRunDiagnostics(
    batchRunId,
    runStatus,
    steps,
    sourcesEnabled,
    configJson
  );

  const failedStep = getEarliestFailedAutoStep(steps);
  const resumableStep = getNextResumableAutoStep(steps, sourcesEnabled);

  // Only show if run needs attention
  if (stuckType !== "IDLE" && stuckType !== "BLOCKED_BY_FAILURE") {
    return null;
  }

  const handleCopyDiagnostics = () => {
    navigator.clipboard.writeText(JSON.stringify(diagnostics, null, 2));
    toast({
      title: "Copied",
      description: "Diagnostic data copied to clipboard",
    });
  };

  const handleRetryFailedStep = async () => {
    if (!failedStep) return;
    setIsRetrying(true);

    try {
      // Set the failed step to ENQUEUED and clear error fields
      const { error: stepError } = await supabase
        .from("cr075_batch_run_steps")
        .update({
          status: "ENQUEUED",
          error_message: null,
          started_at: null,
          finished_at: null,
        })
        .eq("id", failedStep.id);

      if (stepError) throw stepError;

      // Also set the batch run back to RUNNING if it was FAILED
      if (runStatus === "FAILED") {
        await supabase
          .from("cr075_batch_runs")
          .update({ status: "RUNNING" })
          .eq("id", batchRunId);
      }

      // Trigger the scheduler runner to pick up the enqueued step
      await supabase.functions.invoke("scheduler-runner", { body: {} });

      toast({
        title: "Step re-enqueued",
        description: `${failedStep.step_name} (${failedStep.step_key}) will be retried`,
      });

      onRecoveryAction?.();
    } catch (err) {
      console.error("Failed to retry step:", err);
      toast({
        title: "Retry failed",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setIsRetrying(false);
    }
  };

  const handleResumeAutoSteps = async () => {
    if (!resumableStep) return;
    setIsResuming(true);

    try {
      // Set the next resumable step to ENQUEUED
      const { error: stepError, count } = await supabase
        .from("cr075_batch_run_steps")
        .update({
          status: "ENQUEUED",
          started_at: null,
        })
        .eq("id", resumableStep.id)
        .eq("status", "NOT_STARTED");

      if (stepError) throw stepError;

      if (count === 0) {
        toast({
          title: "Already started",
          description: "Step was already enqueued or started",
        });
        onRecoveryAction?.();
        return;
      }

      // Trigger the scheduler runner
      await supabase.functions.invoke("scheduler-runner", { body: {} });

      toast({
        title: "Auto steps resumed",
        description: `${resumableStep.step_name} (${resumableStep.step_key}) enqueued`,
      });

      onRecoveryAction?.();
    } catch (err) {
      console.error("Failed to resume:", err);
      toast({
        title: "Resume failed",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setIsResuming(false);
    }
  };

  const handleSkipStep = async () => {
    if (!failedStep || !skipReason.trim()) return;
    setIsSkipping(true);

    try {
      // Mark the step as SKIPPED with reason
      const { error: stepError } = await supabase
        .from("cr075_batch_run_steps")
        .update({
          status: "SKIPPED",
          skipped_reason: skipReason.trim(),
          finished_at: new Date().toISOString(),
        })
        .eq("id", failedStep.id);

      if (stepError) throw stepError;

      // If the batch run was FAILED, set it back to RUNNING so auto steps can continue
      if (runStatus === "FAILED") {
        await supabase
          .from("cr075_batch_runs")
          .update({ status: "RUNNING" })
          .eq("id", batchRunId);
      }

      toast({
        title: "Step skipped",
        description: `${failedStep.step_name} (${failedStep.step_key}) marked as skipped`,
      });

      setShowSkipDialog(false);
      setSkipReason("");
      onRecoveryAction?.();
    } catch (err) {
      console.error("Failed to skip step:", err);
      toast({
        title: "Skip failed",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setIsSkipping(false);
    }
  };

  // CR164: Check if this is a scheduler-triggered run
  const isSchedulerRun = configJson?.from_schedule === true;
  const approvalCheckpoint = configJson?.approval_checkpoint as string | null;
  
  // For scheduler runs in IDLE state, show more specific messaging
  const getIdleBannerTitle = () => {
    if (isSchedulerRun && resumableStep) {
      return "Waiting for auto-chain";
    }
    return "Auto steps paused";
  };
  
  const getIdleBannerDescription = () => {
    if (isSchedulerRun) {
      if (resumableStep) {
        return (
          <>
            This scheduler run is waiting for <strong>{resumableStep.step_key}</strong> to be picked up. 
            The scheduler-runner will start it automatically. If this persists for more than 30 seconds, 
            click "Resume Auto Steps" to manually trigger.
          </>
        );
      }
      return (
        <>
          This scheduler run completed all AUTO steps up to the <strong>{approvalCheckpoint || "STOP_AFTER_NORMALIZE_DEDUP"}</strong> checkpoint.
          No further auto steps are configured.
        </>
      );
    }
    return (
      <>
        This run is marked <strong>RUNNING</strong>, but no steps are
        currently in progress. You can resume the next auto step.
      </>
    );
  };

  // Render IDLE banner
  if (stuckType === "IDLE") {
    return (
      <TooltipProvider>
        <Alert className={isSchedulerRun ? "border-primary/50 bg-primary/10" : "border-warning/50 bg-warning/10"}>
          {isSchedulerRun ? (
            <Clock className="h-4 w-4 text-primary" />
          ) : (
            <Pause className="h-4 w-4 text-warning" />
          )}
          <AlertTitle className="flex items-center gap-2">
            {getIdleBannerTitle()}
            {isSchedulerRun && (
              <Badge variant="outline" className="text-xs font-normal">
                Scheduler
              </Badge>
            )}
          </AlertTitle>
          <AlertDescription className="mt-2 space-y-3">
            <p className="text-sm text-muted-foreground">
              {getIdleBannerDescription()}
            </p>

            {/* Diagnostic context */}
            <DiagnosticContext
              diagnostics={diagnostics}
              resumableStep={resumableStep}
            />

            {/* Actions */}
            <div className="flex items-center gap-2 pt-1">
              {resumableStep && (
                <Button
                  size="sm"
                  onClick={handleResumeAutoSteps}
                  disabled={isResuming}
                  className="gap-1"
                  variant={isSchedulerRun ? "outline" : "default"}
                >
                  {isResuming ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <PlayCircle className="h-3 w-3" />
                  )}
                  Resume Auto Steps
                </Button>
              )}

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCopyDiagnostics}
                    className="gap-1"
                  >
                    <Copy className="h-3 w-3" />
                    Copy Diagnostics
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Copy full diagnostic JSON to clipboard</p>
                </TooltipContent>
              </Tooltip>
            </div>

            {resumableStep && !isSchedulerRun && (
              <p className="text-xs text-muted-foreground">
                This will enqueue <strong>{resumableStep.step_key}</strong> ({resumableStep.step_name}).
              </p>
            )}
          </AlertDescription>
        </Alert>
      </TooltipProvider>
    );
  }

  // Render BLOCKED_BY_FAILURE banner
  return (
    <TooltipProvider>
      <Alert variant="destructive" className="border-destructive/50 bg-destructive/10">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle className="flex items-center gap-2">
          Needs attention to continue
        </AlertTitle>
        <AlertDescription className="mt-2 space-y-3">
          <p className="text-sm text-muted-foreground">
            An auto step failed:{" "}
            <strong>
              {failedStep?.step_key}: {failedStep?.step_name}
            </strong>
            . Fix the issue and retry, or skip this step to proceed.
          </p>

          {/* Error message if present */}
          {failedStep?.error_message && (
            <div className="bg-destructive/10 rounded-md p-2 border border-destructive/30">
              <p className="text-xs font-mono text-destructive">
                {failedStep.error_message}
              </p>
            </div>
          )}

          {/* Diagnostic context */}
          <DiagnosticContext diagnostics={diagnostics} failedStep={failedStep} />

          {/* Actions */}
          <div className="flex items-center gap-2 pt-1">
            {failedStep && (
              <>
                <Button
                  size="sm"
                  onClick={handleRetryFailedStep}
                  disabled={isRetrying}
                  className="gap-1"
                >
                  {isRetrying ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <RotateCcw className="h-3 w-3" />
                  )}
                  Retry {failedStep.step_key}
                </Button>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowSkipDialog(true)}
                      className="gap-1"
                    >
                      <SkipForward className="h-3 w-3" />
                      Skip {failedStep.step_key}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Marks the step as skipped and continues auto steps</p>
                  </TooltipContent>
                </Tooltip>
              </>
            )}

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCopyDiagnostics}
                  className="gap-1"
                >
                  <Copy className="h-3 w-3" />
                  Copy Diagnostics
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Copy full diagnostic JSON to clipboard</p>
              </TooltipContent>
            </Tooltip>
          </div>

          {/* Microcopy */}
          <div className="text-xs text-muted-foreground space-y-0.5">
            <p>
              <strong>Retry:</strong> Re-enqueues the failed step.
            </p>
            <p>
              <strong>Skip:</strong> Marks the step as skipped and continues
              auto steps. Use only if safe.
            </p>
          </div>
        </AlertDescription>
      </Alert>

      {/* Skip Confirmation Dialog */}
      <AlertDialog open={showSkipDialog} onOpenChange={setShowSkipDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <SkipForward className="h-5 w-5 text-destructive" />
              Skip {failedStep?.step_key}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will mark <strong>{failedStep?.step_name}</strong> as skipped
              and allow auto steps to continue. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-2">
            <Label htmlFor="skip-reason">Reason for skipping (required)</Label>
            <Textarea
              id="skip-reason"
              placeholder="e.g., No merchant sources configured for this run..."
              value={skipReason}
              onChange={(e) => setSkipReason(e.target.value)}
              rows={3}
            />
          </div>

          <AlertDialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowSkipDialog(false);
                setSkipReason("");
              }}
              disabled={isSkipping}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleSkipStep}
              disabled={isSkipping || !skipReason.trim()}
            >
              {isSkipping ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Skipping...
                </>
              ) : (
                <>
                  <SkipForward className="h-4 w-4 mr-2" />
                  Skip Step
                </>
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </TooltipProvider>
  );
}

// Extracted diagnostic context component
function DiagnosticContext({
  diagnostics,
  resumableStep,
  failedStep,
}: {
  diagnostics: ReturnType<typeof buildStuckRunDiagnostics>;
  resumableStep?: BatchRunStep | null;
  failedStep?: BatchRunStep | null;
}) {
  return (
    <div className="bg-background/50 rounded-md p-3 border space-y-2 text-xs">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <span className="text-muted-foreground">Run ID:</span>
          <div className="font-mono">{diagnostics.batch_run_id.slice(0, 8)}</div>
        </div>

        {diagnostics.from_schedule && diagnostics.schedule_run_id && (
          <div>
            <span className="text-muted-foreground">Schedule Run:</span>
            <div className="font-mono">
              <Link
                to={`/admin/scheduler/runs/${diagnostics.schedule_run_id}`}
                className="text-primary hover:underline inline-flex items-center gap-1"
              >
                {diagnostics.schedule_run_id.slice(0, 8)}
                <ExternalLink className="h-3 w-3" />
              </Link>
            </div>
          </div>
        )}

        <div>
          <span className="text-muted-foreground">Sources:</span>
          <div className="flex gap-1 flex-wrap">
            {diagnostics.sources_enabled.map((s) => (
              <Badge key={s} variant="secondary" className="text-xs">
                {s}
              </Badge>
            ))}
          </div>
        </div>

        <div>
          <span className="text-muted-foreground">Stuck Type:</span>
          <div>
            <Badge
              variant={diagnostics.stuck_type === "BLOCKED_BY_FAILURE" ? "destructive" : "outline"}
              className="text-xs"
            >
              {diagnostics.stuck_type}
            </Badge>
          </div>
        </div>
      </div>

      {/* Step status summary */}
      <div className="pt-1">
        <span className="text-muted-foreground">Step Status:</span>
        <div className="flex flex-wrap gap-1 mt-1">
          {Object.entries(diagnostics.step_counts).map(([status, count]) => (
            <Badge
              key={status}
              variant={
                status === "FAILED"
                  ? "destructive"
                  : status === "COMPLETE"
                  ? "outline"
                  : "secondary"
              }
              className="text-xs"
            >
              {status}: {count}
            </Badge>
          ))}
        </div>
      </div>

      {/* Target step info */}
      {(resumableStep || failedStep) && (
        <div className="pt-1 flex items-center gap-2">
          <span className="text-muted-foreground">
            {failedStep ? "Blocker:" : "Next:"}
          </span>
          <Badge variant="outline" className="font-mono">
            {(failedStep || resumableStep)?.step_key}
          </Badge>
          <span>{(failedStep || resumableStep)?.step_name}</span>
          {(failedStep || resumableStep)?.updated_at && (
            <span className="text-muted-foreground flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {formatDistanceToNow(
                new Date((failedStep || resumableStep)!.updated_at!),
                { addSuffix: true }
              )}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
